/**
 * Modal accessibility base (W2: MODAL-01).
 *
 * Gives a dialog the three behaviors every modal needs and none had:
 *  - Escape closes it
 *  - Tab / Shift+Tab cycle inside it (focus trap)
 *  - focus moves into the dialog on open and returns to the trigger on close
 *
 * Usage (inside a component with a `show` ref and a root element ref):
 *   const modalRoot = ref(null);
 *   useModalA11y(show, { rootRef: modalRoot, onEscape: cancel });
 * Template root: <div ref="modalRoot" role="dialog" aria-modal="true" ...>
 */

import { watch, nextTick, onBeforeUnmount } from 'vue';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useModalA11y(showRef, { rootRef, onEscape } = {}) {
  let previouslyFocused = null;

  function focusables() {
    const root = rootRef?.value;
    if (!root) return [];
    return [...root.querySelectorAll(FOCUSABLE_SELECTOR)]
      .filter((el) => el.offsetParent !== null);
  }

  function handleKeydown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (onEscape) onEscape();
      return;
    }
    if (e.key === 'Tab') {
      const els = focusables();
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !els.includes(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function detach() {
    document.removeEventListener('keydown', handleKeydown, true);
  }

  watch(showRef, async (open) => {
    if (open) {
      previouslyFocused = document.activeElement;
      document.addEventListener('keydown', handleKeydown, true);
      await nextTick();
      const els = focusables();
      if (els.length) els[0].focus();
      else rootRef?.value?.focus?.();
    } else {
      detach();
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
      previouslyFocused = null;
    }
  });

  onBeforeUnmount(detach);
}

export default useModalA11y;
